import React, {useContext, useEffect, useState, useRef} from "react";

import {Link} from "react-router-dom";


import * as ApiCalls from "../ApiCalls.js";
import Loading from "../Loading";
import {PaginationTaskTable} from "./PaginationTaskTable";
import SelectTargetUser from "./SelectTargetUser";
import TableTitleTree from "./TableTitleTree";
import DueDate from "./DueDate";
import LovdataLink from "./LovdataLink";
import SyncId from "./SyncId";

import ActionButton from "./ActionButtons/ActionButton";

import {RolesEnum} from "../../../Types/RolesEnum";

import {azureTokenContext, userIdContext} from "../../../App";

import {TextField, IconButton, InputAdornment} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import ClearIcon from "@mui/icons-material/Clear";
import {VscListFilter} from "react-icons/vsc";


import "../../../style/PriorityTableStyles/taskstable.css";
import {targetUserIdContext} from "../../MainPart";
import OpenDocumentDropdown from "./OpenDocumentDropdown";
import {StatusEnum} from "../../../Types/StatusEnum";
import {TaskType} from "../../../Types/Task";

const FilterIcon = VscListFilter as React.ElementType;

export default function MainTasksTable() {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const [targetUserId] = useContext(targetUserIdContext);

    const [allTasks, setAllTasks] = useState<TaskType[]>([]);
    const [selectedTasks, setSelectedTasks] = useState<TaskType[]>([]);

    const visibleResultNumber = 8;
    const [pageCounter, setPageCounter] = useState<number>(0);
    const [totalPageNumber, setTotalPageNumber] = useState<number>(1);

    const [loadingStatus, setLoadingStatus] = useState("loading");

    const [searchValue, setSearchValue] = useState("");

    // Filter states
    const [activeFilter, setActiveFilter] = useState<string | null>(null);

    const [uniqueDocumentNames, setUniqueDocumentNames] = useState<string[]>([]);
    const [uniqueRoles, setUniqueRoles] = useState<string[]>([]);
    const [uniqueDescriptions, setUniqueDescriptions] = useState<string[]>([]);

    const [selectedDocumentNames, setSelectedDocumentNames] = useState<string[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedDescriptions, setSelectedDescriptions] = useState<string[]>([]);

    // Refs for dropdown positions
    const documentNameFilterRef = useRef<HTMLDivElement>(null);
    const roleFilterRef = useRef<HTMLDivElement>(null);
    const descriptionFilterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLoadingStatus("loading");
        ApiCalls.getPrioritizedTasks(targetUserId, azureToken)
            .then((res) => {
                const convertedTasks = convertTasks(res.TaskPriorities);

                setAllTasks(convertedTasks);
                setSelectedTasks(convertedTasks);


                // Extract unique values for filters
                const documentNames = [...new Set(convertedTasks.map(task => task.DocumentTitle))]
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'nb-NO'));
                const roles = [...new Set(convertedTasks.map(task => mapRole(task.Role)))]
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'nb-NO'));
                const descriptions = [...new Set(convertedTasks.map(task => task.Description))]
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'nb-NO'));
                setUniqueDocumentNames(documentNames);
                setUniqueRoles(roles);
                setUniqueDescriptions(descriptions);

                setTotalPageNumber(Math.ceil(convertedTasks.length / visibleResultNumber));
                setPageCounter(1);

                setLoadingStatus("done");
                log("INFO", "Success getPrioritizedTasks", "AuthorsPortal", "", "");
                logMetric("AuthorsPortal", "", "Success", "", "Successful getPrioritizedTasks", "", "");
            })
            .catch((err) => {
                console.error("get Tasks fail", err);
                setLoadingStatus("error");
                log("ERROR", "Failed getPrioritizedTasks", "AuthorsPortal", "", "");
                logMetric("AuthorsPortal", "", "Failed", "", "Failed getPrioritizedTasks in AuthorsPortal", "", "");
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, targetUserId]);

    useEffect(() => {
        handleSearchClear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUserId, userId]);

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, selectedDocumentNames, selectedRoles, selectedDescriptions]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (activeFilter &&
                documentNameFilterRef.current &&
                roleFilterRef.current &&
                descriptionFilterRef.current &&
                !documentNameFilterRef.current.contains(event.target as Node) &&
                !roleFilterRef.current.contains(event.target as Node) &&
                !descriptionFilterRef.current.contains(event.target as Node)) {
                setActiveFilter(null);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activeFilter]);

    const ShowDocumentLinks = (task: TaskType) => {
        if ((task.WorkItemId || task.AuthorCreatedWI) && task.WorkItemStatus !== StatusEnum.readyForPublish) {
            return (
                <span>
                        <OpenDocumentDropdown row={task}/>
                    </span>
            );
        }
        return "";
    }

    const handleSearchClear = () => {
        setSearchValue("");
        setSelectedDocumentNames([]);
        setSelectedRoles([]);
        setSelectedDescriptions([]);
        setSelectedTasks(allTasks);
        setTotalPageNumber(Math.ceil(allTasks.length / visibleResultNumber));
        setPageCounter(1);
    };

    const handleSearchInputChange = (event) => {
        setSearchValue(event.target.value);
    };

    const applyFilters = () => {
        let filteredTasks = allTasks;

        // Apply text search filter
        if (searchValue) {
            filteredTasks = filteredTasks.filter((item) =>
                Object.values(item).some((value) =>
                    String(value).toLowerCase().includes(searchValue.toLowerCase())
                )
            );
        }

        // Apply Role filter
        if (selectedDocumentNames.length > 0) {
            filteredTasks = filteredTasks.filter(task => selectedDocumentNames.includes(task.DocumentTitle));
        }

        if (selectedRoles.length > 0) {
            filteredTasks = filteredTasks.filter(task => selectedRoles.includes(mapRole(task.Role)));
        }

        // Apply Description filter
        if (selectedDescriptions.length > 0) {
            filteredTasks = filteredTasks.filter(task => selectedDescriptions.includes(task.Description));
        }

        setSelectedTasks(filteredTasks);
        setTotalPageNumber(Math.ceil(filteredTasks.length / visibleResultNumber));
        setPageCounter(1);
    };

    // Filter handlers
    const toggleFilter = (filterName: string) => {
        if (activeFilter === filterName) {
            setActiveFilter(null);
        } else {
            setActiveFilter(filterName);
        }
    };

    const handleFilterChange = (item: string, filterType: string) => {
        if (filterType === 'documentName') {
            setSelectedDocumentNames(prev =>
                prev.includes(item)
                    ? prev.filter(n => n !== item)
                    : [...prev, item]
            );
        } else if (filterType === 'role') {
            setSelectedRoles(prev =>
                prev.includes(item)
                    ? prev.filter(r => r !== item)
                    : [...prev, item]
            );
        } else if (filterType === 'description') {
            setSelectedDescriptions(prev =>
                prev.includes(item)
                    ? prev.filter(d => d !== item)
                    : [...prev, item]
            );
        }
    };

    const handleRemoveAllFilters = (items: string[], selectedItems: string[], filterType: string) => {
        if (filterType === 'documentName') {
            setSelectedDocumentNames([]);
        } else if (filterType === 'role') {
            setSelectedRoles([]);
        } else if (filterType === 'description') {
            setSelectedDescriptions([]);
        }
    };

    const makeClassName = (originalClass: string, prioritynumber: number) => {
        if (prioritynumber === 0) {
            return `${originalClass} prioritized`;
        }
        return originalClass;
    };

    const FilterDropdown = ({
                                items,
                                selectedItems,
                                filterType,
                                isOpen,
                                toggleFilter,
                                handleFilterChange,
                                handleRemoveAllFilters,
                                reference
                            }) => {
        return (
            <div ref={reference} className="relative ml-1 inline-block">
                <FilterIcon
                    onClick={() => toggleFilter(filterType)}
                    className={`FilterIcon cursor-pointer ${selectedItems.length > 0 ? 'text-blue-600' : 'text-gray-700'}`}
                />
                {isOpen && (
                    <div
                        className="FilterModal absolute left-0 z-10 max-h-80 shadow-lg overflow-hidden flex flex-col">
                        <div className="overflow-y-auto max-h-64 p-2">
                            {items.map(item => (
                                <div key={item} className="mb-1">
                                    <label className="flex items-center p-1 hover:bg-gray-100 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(item)}
                                            onChange={() => handleFilterChange(item, filterType)}
                                            className="mr-2 h-4 w-4"
                                        />
                                        <span className="text-sm text-gray-700 truncate">{item}</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between p-2 border-t border-gray-200">
                            <button
                                className="FilterButtons px-3 py-1 text-xs"
                                onClick={() => toggleFilter(filterType)}
                            >
                                Lukk
                            </button>
                            {selectedItems.length > 0 && (
                                <button
                                    className="FilterButtons px-3 py-1 text-xs"
                                    onClick={() => handleRemoveAllFilters(items, selectedItems, filterType)}
                                >
                                    Fjern alle
                                </button>
                            )
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userId, azureToken);
    };

    const logMetric = (category, subCategory, status, tag, message, workItemId, userRole) => {
        ApiCalls.logMetric(category, subCategory, status, tag, message, workItemId, userRole, userId, azureToken);
    };

    return (
        <>
            <>{loadingStatus === "loading" && <div className="loading-container"><Loading/></div>}</>
            <>
                {loadingStatus === "done" && (
                    <>
                        <div className="tasks-table-container">
                            <SelectTargetUser/>
                            <div className="tasks-title-row">
                                <div className="text-2xl font-semibold">Oppgaver</div>
                                <div className="search-container mr-5">
                                    <TextField
                                        className="search-box"
                                        placeholder="Søk i oppgaver"
                                        variant="standard"
                                        value={searchValue}
                                        onChange={handleSearchInputChange}
                                        sx={{display: "inline-block"}}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {searchValue && (
                                                        <IconButton onClick={handleSearchClear} edge="end">
                                                            <ClearIcon/>
                                                        </IconButton>
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>
                            </div>
                            <table className="tasks-table">
                                <thead>
                                <tr>
                                    <th className="table-head-task-name">
                                        Publikasjon
                                        <FilterDropdown
                                            items={uniqueDocumentNames}
                                            selectedItems={selectedDocumentNames}
                                            filterType="documentName"
                                            isOpen={activeFilter === 'documentName'}
                                            toggleFilter={toggleFilter}
                                            handleFilterChange={handleFilterChange}
                                            handleRemoveAllFilters={handleRemoveAllFilters}
                                            reference={documentNameFilterRef}
                                        />
                                    </th>
                                    <th className="table-head-task-role">
                                        Rolle
                                        <FilterDropdown
                                            items={uniqueRoles}
                                            selectedItems={selectedRoles}
                                            filterType="role"
                                            isOpen={activeFilter === 'role'}
                                            toggleFilter={toggleFilter}
                                            handleFilterChange={handleFilterChange}
                                            handleRemoveAllFilters={handleRemoveAllFilters}
                                            reference={roleFilterRef}
                                        />
                                    </th>
                                    <th className="table-head-description">
                                        Min oppgave
                                        <FilterDropdown
                                            items={uniqueDescriptions}
                                            selectedItems={selectedDescriptions}
                                            filterType="description"
                                            isOpen={activeFilter === 'description'}
                                            toggleFilter={toggleFilter}
                                            handleFilterChange={handleFilterChange}
                                            handleRemoveAllFilters={handleRemoveAllFilters}
                                            reference={descriptionFilterRef}
                                        />
                                    </th>
                                    <th className="table-head-link"></th>
                                    <th className="table-head-action"></th>
                                    <th className="table-head-duedate">Frist</th>
                                    <th className="table-head-lovdata-link">Lovdata</th>
                                    <th className="table-head-syncid">ID</th>
                                </tr>
                                </thead>
                                {selectedTasks && selectedTasks.length === 0 && (
                                    <tbody>
                                    <tr>
                                        <td colSpan={7}>
                                            <p className="no-results-message">{searchValue || selectedRoles.length > 0 || selectedDescriptions.length > 0 ? "Ingen resultater" : "Ingen oppgaver"}</p>
                                        </td>
                                    </tr>
                                    </tbody>
                                )}
                                {selectedTasks && selectedTasks.length > 0 && (
                                    <tbody>
                                    {selectedTasks
                                        .slice((pageCounter - 1) * visibleResultNumber, pageCounter * visibleResultNumber)
                                        .map((row: TaskType) => (
                                            <tr key={row.TaskId}>
                                                <td className={makeClassName("table-body-task-name", row.PriorityNumber)}>
                                                    <TableTitleTree task={row}/>
                                                </td>
                                                <td className={makeClassName("table-body-task-role", row.PriorityNumber)}>{mapRole(row.Role)}</td>
                                                <td className={makeClassName("table-body-description", row.PriorityNumber)}>{row.Description}</td>
                                                <td className={makeClassName("table-body-link", row.PriorityNumber)}>
                                                    {ShowDocumentLinks(row)}
                                                </td>
                                                <td className="table-body-action">
                                                    <ActionButton task={row}
                                                                  setAllTasks={setAllTasks}
                                                                  setSelectedTasks={setSelectedTasks}
                                                    />
                                                </td>
                                                <td className={makeClassName("table-body-duedate", row.PriorityNumber)}>
                                                    <DueDate duedate={row.DueDate}/>
                                                </td>
                                                <td className={makeClassName("table-body-lovdata-link", row.PriorityNumber)}>
                                                    <LovdataLink lovdataUrl={row.LovdataUrl}/>
                                                </td>
                                                <td className={makeClassName("table-body-syncid", row.PriorityNumber)}>
                                                    <SyncId syncId={row.SyncId}/>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                )}
                            </table>

                            <div className="table-footer-main">
                                <PaginationTaskTable pageCounter={pageCounter} totalPageNumber={totalPageNumber}
                                                     setPageCounter={setPageCounter}/>
                                <a className="all-tasks-link" target="_blank" rel="noopener noreferrer"
                                   href="https://karnov.tawk.help/">Hjelp og veiledning til forfatterportalen</a>
                                <Link
                                    className="all-tasks-link"
                                    to="/publishedtasks">
                                    Se tidligere oppgaver
                                </Link>
                            </div>
                        </div>
                    </>
                )}
            </>
            <>{loadingStatus === "error" && (
                <div className="error-container">
                    <h3>Det oppstod en feil.</h3>
                    <p>Kunne ikke hente oppgaver. Vennligst last siden på nytt.</p>
                </div>
            )}</>
        </>
    );
}

export function convertTasks(tasks: any) {
    // First, sort tasks by Priority
    const sortedTasks = [...tasks].sort((a, b) => b.Priority - a.Priority);

    return sortedTasks.map((item, index) => {
        return {
            ...item,
            PriorityNumber: index, // Assuming PriorityNumber is the index in the sorted array
        };
    });
}

export function mapRole(role: string) {
    if (!role) return "Ukjent";
    switch (role) {
        case RolesEnum.author:
            return "Forfatter";
        case RolesEnum.contentEditor:
            return "Fagredaktør";
        case RolesEnum.karnovEditor:
            return "Karnov-redaktør";
        case RolesEnum.overrideAuthor:
            return "Medforfatter";
        default:
            return role;
    }
}
